import { ApiProject, Configuration, ConfigurationData, Entity, Leg, LegPackage, Material, Module, Product, ProductCategory, Project, ProjectFilter, Series, SeriesData, SeriesFilter, Texture, YUIContext } from "./types/type_def";
import { MathHelper, MeshHelper, ModuleHelper, SeriesHelper } from "./helpers";
import { ProceduralTextureFromDOM } from "./ProceduralTextureFromDOM";
import { AppSettings } from "./settings";

/**
 * Project Library
 * */

export class ProjectLibrary {

    projects: Array<Project>
    count: number

    constructor() {
        this.projects = [];
        this.count = 0;
    }

    async getAllProjects(projectFilter: ProjectFilter) {

        let filter = "";
        let filters = [];

        if (projectFilter.hideInactive) {
            filters.push("Status ne 0");
        }

        if (projectFilter.search.length > 0) {
            filters.push(`contains(toLower(Name), toLower('${projectFilter.search}'))`);
        }

        if (filters.length > 0) {
            filter = `$filter=${filters.join(" and ")}`;
        }

        let top = projectFilter.itemsShown;
        let skip = (projectFilter.currentPage - 1) * projectFilter.itemsShown;

        var response = await fetch(`odata/projects?${filter}&$top=${top}&$skip=${skip}&$count=true&$orderby=Added desc`, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                "Authorization": "Bearer " + localStorage.getItem("accessToken")
            },
        });

        const apiprojects = await response.json() as {value: ApiProject[]};
        const projects: Project[] = apiprojects.value.map(p => ({
            ...p,
            configurations: p.configurations.sort(s => s.key).map(c => ({   
                key: c.key,           
                series: c.series,
                data: JSON.parse(c.data) as ConfigurationData
            }))
        }));

        this.count = apiprojects["@odata.count"];
        this.projects = projects;
    }

    async getProject(identifier: string): Promise<Project> {

        var response = await fetch("odata/projects/" + identifier, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                "Authorization": "Bearer " + localStorage.getItem("accessToken")
            },
        });

        const data = await response.json();
        const apiproject = data as ApiProject;
        const project = {
            ...apiproject,
            configurations: apiproject.configurations.sort((a,b) => a.key < b.key ? -1 : a.key > b.key ? 1 : 0).map(c => ({  
                key: c.key,                    
                series: c.series,
                data: JSON.parse(c.data) as ConfigurationData
            }))
        }

      //  this.projects.push(project);

        return project;
    }

    async createProject(project: Project): Promise<Project> {

        var modifiedProject = JSON.parse(JSON.stringify(project));

        //Serialize data
        modifiedProject.configurations.forEach(function (configuration) {
            configuration.data = JSON.stringify(configuration.data);
        });

        var response = await fetch("odata/projects", {
            method: "POST",
            body: JSON.stringify(modifiedProject),
            headers: {
                "Content-Type": "application/json",
                "Authorization": "Bearer " + localStorage.getItem("accessToken")
            },
        });

        const data = await response.json();
        const apiproject = data as ApiProject;
        const newProject = {
            ...apiproject,
            configurations: apiproject.configurations.map(c => ({    
                key: c.key,            
                series: c.series,
                data: JSON.parse(c.data) as ConfigurationData
            }))
        }

        this.projects.push(newProject);

        return newProject;
    }

    async saveProject(project: Project) {

        var modifiedProject = JSON.parse(JSON.stringify(project));

        //Serialize data
        modifiedProject.configurations.forEach(function (configuration) {
           // configuration.key = configuration.key;
            configuration.data = JSON.stringify(configuration.data);
        });

        var response = await fetch("odata/projects/" + modifiedProject.identifier, {
            method: "PUT",
            body: JSON.stringify(modifiedProject),
            headers: {
                "Content-Type": "application/json",
                "Authorization": "Bearer " + localStorage.getItem("accessToken")
            },
        });

        const data = await response.json();
        const apiproject = data as ApiProject;
        const updatedProject = {
            ...apiproject,
            configurations: apiproject.configurations.map(c => ({      
                key: c.key,                    
                series: c.series,
                data: JSON.parse(c.data) as ConfigurationData
            }))
        }

        return updatedProject;
    }

    async deleteProject(identifier: string) {
         var response = await fetch("odata/projects/" + identifier, {
            method: "DELETE",           
            headers: {
                "Content-Type": "application/json",
                "Authorization": "Bearer " + localStorage.getItem("accessToken")
            },            
        });

        if (response.status === 220) {
            return true;
        }

        return false;
    }

    getConfiguration(project: Project, configurationId: any) {
        return project.configurations.find(function (config) { return config.data.id === configurationId });
    }

    deleteConfiguation(project: Project, configuration: Configuration) {
        let index = project.configurations.indexOf(configuration);
        project.configurations.splice(index, 1);
    }

    createConfiguration(project: Project, configuration: Configuration) {

        project.configurations.push(configuration);
        return configuration;

    }


}


/**
 * Configuration Library  
 * */
export class ConfigurationLibrary {

    configurations: Array<Configuration>

    constructor() {
        this.configurations = [];
    }

    addConfiguration(configuration: Configuration) {
        this.configurations.push(configuration);
    }

    removeConfiguration(configuration: Configuration) {
        let index = this.configurations.indexOf(configuration);
        this.configurations.splice(index, 1);
    }

    getConfiguration(configurationId: string) {
        return this.configurations.find(function (c) { return c.data.id === configurationId });
    }

    setConfigurations(configurations: Array<Configuration>) {
        this.configurations = configurations;
    }

}

/**
* Series Library
* */
export class SeriesLibrary {

    series?: Series[];
    seriesData: SeriesData[];

    constructor() {
        this.series = undefined;
        this.seriesData = [];
    }

    async fetchSeries(filter: SeriesFilter): Promise<Series[]> {
        /*if(this.series != undefined){
            return this.series;
        }*/

        var response = await fetch(`api/series?query=${filter.search}`, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                "Authorization": "Bearer " + localStorage.getItem("accessToken")
            },
        });

        let data = await response.json() as Series[];
        this.series = data;

        return data;
    }

    async fetchSeriesByName(name: string): Promise<SeriesData> {
        if (this.seriesData.some(x => x.name.toLocaleLowerCase() == name.toLocaleLowerCase())) {
            return this.seriesData.find(x => x.name.toLocaleLowerCase() == name.toLocaleLowerCase());
        }

        let id = this.series.find(e => e.name.toLocaleLowerCase() == name.toLocaleLowerCase()).id;

        var response = await fetch(`api/series/${id}`, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                "Authorization": "Bearer " + localStorage.getItem("accessToken")
            },
        });

        let data = await response.json();
        let series = data as SeriesData;
        this.seriesData.push(series);

        return series;
    }

    async fetchSeriesByID(id: number): Promise<SeriesData> {
        if (this.seriesData.some(x => x.id == id)) {
            return this.seriesData.find(x => x.id === id);
        }

        var response = await fetch(`api/series/${id}`, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                "Authorization": "Bearer " + localStorage.getItem("accessToken")
            },
        });

        let data = await response.json();
        let series = data as SeriesData;
        this.seriesData.push(series);

        return series;
    }

    getSeriesById(id: number): SeriesData {
        return this.seriesData.find(function (x) { return x.id === id });
    }

    getSeriesByName(name: string): SeriesData {
        return this.seriesData.find(function (x) { return x.name.toLocaleLowerCase() === name.toLocaleLowerCase() });
    }

    getLegThumbnail(series: SeriesData, legPackageType: string, legType?: string): string {
        console.log(legPackageType);
        let leg = this.getSeriesLeg(series, legPackageType, legType);
        if (leg) {
        return AppSettings.Paths.BaseModelsPath + leg.thumbnail + "_thumb.jpg";
        }

        return "";
    }

    getSeriesLeg(series: SeriesData, identifier: string, legType?: string): Leg {
        let legPackage = this.getSeriesLegPackage(series, identifier);

        if (!legType) {
            return legPackage.legs[0];
        }

        return legPackage.legs.find(x => x.type.identifier.toLowerCase() === legType.toLowerCase());
    }

    getSeriesLegPackage(series: SeriesData, identifier: string): LegPackage {
        let legPackage = series.legPackages.find(x => x.identifier.toLowerCase() === identifier.toLowerCase());

        if(!legPackage){
            legPackage = series.legPackages.find(x => x.type.toLowerCase() == "standard");
        }

        return legPackage;
    }

    getMaterialByID(seriesId: number, productNumber: string) {
        var validSeries = this.seriesData.find(e => e.id == seriesId);
        return validSeries.materials.find(function (material) { return material.productNumber === productNumber });
    }

}

/**
* Product Library
* */
export class ProductLibrary {

    productsCategories: Array<ProductCategory>

    constructor() {
    }

    /**
     * Fetch products from datastore by series
     * @param seriesId
     */
    async fetchProducts(seriesId: number,) {

        var response = await fetch("api/series/" + seriesId + "/products", {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                "Authorization": "Bearer " + localStorage.getItem("accessToken")
            },
        });

        var data = await response.json();
        this.productsCategories = data;
    }

    getProductCategory(productCategory: string) {

        switch (productCategory) {
            case "SOFA":
            case "SOFA_LEFT":
            case "SOFA_RIGHT":
            case "CORNER_SECTION":
            case "CLOSING_CORNER_SECTION_RIGHT":
            case "CLOSING_CORNER_SECTION_LEFT":
            case "CENTER_SECTION":
            case "CENTER_SECTION_LEFT_SHORTBACK":
            case "CENTER_SECTION_RIGHT_SHORTBACK":
            case "CENTER_SECTION_WO_BACK":
            case "CLOSING_SECTION_LEFT":
            case "CLOSING_SECTION_RIGHT":
            case "CLOSING_SECTION_LEFT_SHORTBACK":
            case "CLOSING_SECTION_RIGHT_SHORTBACK":
            case "CLOSING_SECTION_RIGHT_WO_ARM":
            case "CLOSING_SECTION_LEFT_WO_ARM":
            case "CENTER_SECTION_INSIDE_30_STRAIGHT":
            case "CENTER_SECTION_INSIDE_30_CURVED":
            case "CHAISELONG_LEFT_WO_ARM":
            case "CHAISELONG_RIGHT_WO_ARM":
            case "SOFA_SECTIONAL":
            case "BASE_MODULE": 
            case "BASE_MODULE_WITH_SEAT_CUSHION":
            case "COMBI_SECTION_LEFT_SHORTBACK":
            case "COMBI_SECTION_RIGHT_SHORTBACK":
            case "CHAIR":
            case "CHAIR_LEFT":
            case "CHAIR_RIGHT":
            case "ARM":
            case "ARM_L":
            case "ARM_R":
            case "SEATFRAME":
            case "DOUBLE_SECTION":
            case "BACKREST":
            case "CHAISELONG_LEFT":
            case "CHAISELONG_RIGHT":
            case "BACK":
            case "FOOTSTOOL": {
                return "UPHOLSTRY";
            }
            default: {
                return null;
            }
        }

    }

    /**
     * Gets products by product group
     * @param productCategoryId
     */
    getProductsByGroup(productCategoryId: string) {

        let validProducts = [] as Array<Product>;

        var groupProducts = this.productsCategories.find(function (productGroup) { return productGroup.type === productCategoryId });
        if (groupProducts) {
            validProducts = groupProducts.products;
        }

        return validProducts;
    }


    /**
    * Gets product by productnumber
    * @param productNumber
    */
    getProductByProductNo(productNumber: string | number) {

        for (let pc of this.productsCategories) {
            if (pc.products) {
                for (let p of pc.products) {
                    if (p.productNo === productNumber) {
                        return p;
                    }
                }
            }
        }

        return null;
    }

    getProductPrice(productNumber: string, priceGroup: string) {

        var product = this.getProductByProductNo(productNumber);
        if (product) {

            var cushionPackage = product.cushionPackages.find(x => x.isDefault);
            var productVariant = cushionPackage.variants[0];

            var currency = productVariant.pricelists[0]?.currency ?? "";
            var price = productVariant.pricelists[0]?.prices[priceGroup] ?? 0;

            return {
                currency, price
            }

        }

    }

    getProductVariantByConnectionType(product: Product, connectionType: string) {

        var cushionPackage = product.cushionPackages.find(x => x.isDefault);
        var productVariant = cushionPackage.variants.find(x => x.connectionType === connectionType.split("_")[0]);
        return productVariant;

    }

    getFirstProductVariant(product: Product) {
        var cushionPackage = product.cushionPackages.find(x => x.isDefault);
        var productVariant = cushionPackage.variants[0];
        return productVariant;
    }

    getProductVariantByProductNo(product: Product) {
        var cushionPackage = product.cushionPackages.find(x => x.isDefault);
        var productVariant = cushionPackage.variants.find(x => x.productNo === product.productNo);
        return productVariant;
    }

    hasProductVariants = function (product: Product, variants: string[]) {
        var standardCushionPackage = product.cushionPackages.find(x => x.isDefault);
        return variants.filter(x => { return standardCushionPackage.variants.map(m => m.connectionType).includes(x) }).length > 0;
    }

}

/**
 * Materials Library
 * */
export class MaterialLibrary {

    defaultMaterial: string;
    materials: Array<Material>
    textures: Array<Texture>
    dataUrls: Map<string, string>

    constructor() {
        this.defaultMaterial = "";
        this.materials = [] as Array<Material>;
        this.textures = [] as Array<Texture>;
    }

    getDefaultMaterial(): Material {
        var self = this;
        return this.materials.find(function (material) { return material.productNumber === self.defaultMaterial });
    }       

    getMaterialGroups() {
        var materialGroups = this.materials.map(function (material) { return material.group });
        var uniqueGroups = MathHelper.getUniqueValues(materialGroups);
        uniqueGroups = uniqueGroups.sort((a,b) => a > b ? 1 : a < b ? -1 : 0);
        return uniqueGroups;
    }

    getMaterialsByGroup(groupId: string) : Material[] {

        if (!groupId) {
            groupId = this.materials[0].group
        }

        return this.materials.filter(function (material) { return material.group === groupId });
    }

    getMaterialByProductNumber(productNumber: string) : Material {

        let material;
        if (productNumber) {
            material = this.materials.find(function (material) { return material.productNumber === productNumber });
        }

        if (!material) {
            material = this.getDefaultMaterial();
        }

        return material;
    }

    getMaterialThumbnailUrl(productNumber: string): string {
        let material = this.getMaterialByProductNumber(productNumber);
        return AppSettings.Paths.BaseMaterialsPath + material.type + "/" + material.group + "/" + material.productNumber + "_thumb.jpg";
    }


    getMaterialByID(materialId: string): Material {
        if (materialId) {
            return this.materials.find(function (m) { return m.id === materialId });
        }
        return this.getDefaultMaterial();
    }  

    getMaterialFilePath = (materialType: string, product: Product, material: Material) => {

        let materialFlipped = "";

        //if ([148882,157547,157546].includes(product.series)) {
            materialFlipped = (product.isFlipped ? "_flip" : "");
        //}

        let filePath = `${AppSettings.Paths.BaseMaterialsPath}${material.type}/${material.group}/${material.productNumber}_${materialType}${materialFlipped}.jpg`;
      
        return filePath;        
    }

    getAOFilePath(product: Product) {     
        const filePath = `${AppSettings.Paths.BaseModelsPath}furniture/${product.model}_AO.png`;
        return filePath;
    }

    assignMaterial(Y: YUIContext, product: Product, meshes: Array<BABYLON.TransformNode>, material: Material, scene: BABYLON.Scene, callback?: any) {

        const self = this;
        let updateMaterialIds = [];

        meshes.forEach(function (mesh) {
            let validChildMeshes = mesh.getChildMeshes(false, function (m) { return m["material"] && !m.name.includes("_NOMAT") && (m.metadata && m.metadata.meshtype !== "leg") });
            validChildMeshes.forEach(function (cm) {

                let materialId = cm.material.uniqueId;
                if (!updateMaterialIds.includes(materialId)) {
                    updateMaterialIds.push(materialId);
                }

            }, this);
        }, this);

        const onPropertiesLoaded = (materialIds) => {           

            const albedoFilePath = self.getMaterialFilePath("albedo", product, material);
            const detailFilePath = self.getMaterialFilePath("detail", product, material);

            //Detail
            let detailMapTexture;
            if (material.properties.detailbump > 0) {                          
            
                self.getTextureBlobURL(detailFilePath, function (detailMapUrl) {
                
                    detailMapTexture = scene.textures.find(x => x.name === "CustomProceduralTexture_" + material.type + "/" + material.group) as ProceduralTextureFromDOM;
                    if (!detailMapTexture) {
                        detailMapTexture = new ProceduralTextureFromDOM("CustomProceduralTexture_" + material.type + "/" + material.group, { fragmentElement: "CopyRtoAPixelShader" }, 1024, scene);                     
                        detailMapTexture.setTexture("detailmapjpeg", new BABYLON.Texture(detailMapUrl, scene));
                        detailMapTexture.refreshRate = 0;
                    }

                   materialIds.forEach(function (materialId, index) {                                        
                            
                        let meshMaterial = scene.getMaterialByUniqueID(materialId) as BABYLON.PBRMaterial;
                        if (meshMaterial) {
                            
                            meshMaterial.detailMap.texture = detailMapTexture;
                            (meshMaterial.detailMap.texture as BABYLON.Texture).uScale = material.properties.tileU;
                            (meshMaterial.detailMap.texture as BABYLON.Texture).vScale = material.properties.tileV;
                            (meshMaterial.detailMap.texture as BABYLON.Texture).coordinatesIndex = 0; // Use same UV coords as albedo
                             meshMaterial.detailMap.bumpLevel = material.properties.detailbump;
                            meshMaterial.detailMap.roughnessBlendLevel = 0;
                            meshMaterial.detailMap.diffuseBlendLevel = 0;
                            meshMaterial.detailMap.isEnabled = (material.properties.detailbump > 0);

                            // Y.log("detail texture updated for material: " + meshMaterial.name + " (" + meshMaterial.uniqueId + ")");
                        }
                    });             
                });
            }

            //Albedo
            this.getTextureBlobURL(albedoFilePath, function (albedoMapUrl) {

                if (materialIds.length <= 0) {
                    Y.log("assignmaterial - No meshes updatetable");
                } else {                    

                    materialIds.forEach(function (materialId, index) {

                        let meshMaterial = scene.getMaterialByUniqueID(materialId) as BABYLON.PBRMaterial;
                        if (meshMaterial) {

                            //Albedo
                            meshMaterial.albedoColor = BABYLON.Color3.White();                           
                         
                           // var albedoTexture = scene.textures.find(x => (x as BABYLON.Texture).name = albedoMapUrl);
                           // if (!albedoTexture) {
                            const albedoTexture = new BABYLON.Texture(albedoMapUrl, scene, false, false);
                            (albedoTexture as BABYLON.Texture).uScale = material.properties.tileU;
                            (albedoTexture as BABYLON.Texture).vScale = material.properties.tileV;
                           // }
                            meshMaterial.albedoTexture = albedoTexture;
                                                    
                            meshMaterial.roughness = material.properties.roughness;
                            meshMaterial.metallic = material.properties.metalness;

                            //Sheen
                            if (meshMaterial.sheen) {
                                meshMaterial.sheen.isEnabled = material.properties.sheen > 0;
                                meshMaterial.sheen.intensity = material.properties.sheen;
                            }

                            //AO Map
                            if (material.properties.ao > 0) {
                              
                                self.getTextureBlobURL(self.getAOFilePath(product), function (aoMapUrl) {

                                    //var aoTexture = scene.textures.find(x => (x as BABYLON.Texture).name = aoMapUrl);
                                   // if (!aoTexture) {
                                    const aoTexture = new BABYLON.Texture(aoMapUrl, scene, false, false);
                                  //  }
                                    meshMaterial.ambientTexture = aoTexture;                                    
                                    (meshMaterial.ambientTexture as BABYLON.Texture).uScale = 1;
                                    (meshMaterial.ambientTexture as BABYLON.Texture).vScale = 1;
                                    (meshMaterial.ambientTexture as BABYLON.Texture).coordinatesIndex = 1; // Use same UV coords as albedo
                                    meshMaterial.ambientTextureStrength = 1;
                                });
                            }
                       
                            if (typeof (callback) === "function") {                                     
                                callback();
                            }        

                        }
                    });
                }

            });
        }

        if(!material.properties) {
            material.properties = {
                metalness: 0,
                roughness: 1,
                tileU: 1,
                tileV: 1,
                sheen: 0,
                detailbump: 0,
                ao: 0
            };

            fetch(AppSettings.Paths.BaseMaterialsPath + material.type + "/" + material.group + "/" + material.productNumber + "_data.json").then((response) => {
                return response.json();
            }).then((data) => {
                for (const key in data) {
                    material.properties[key] = data[key];
                }
            }).catch((error) => {
                return error;
            }).finally(() => {
                onPropertiesLoaded(updateMaterialIds);
            })
        } else {
            onPropertiesLoaded(updateMaterialIds);
        }
    }

    /**
     * Checks to see if texture exists in library by filename, otherwise loads given texture
     * 
     * @param filePath
     * @param callback
     */
    getTextureBlobURL(filePath: string, callback: any) {

        var self = this;

        var texture = self.textures.find(x => x.filepath.toLowerCase() === filePath.toLowerCase());

        if (texture) {
            callback(texture.blobUrl)
        } else {

            fetch(filePath).then((response) => response.blob())
            .then((blob) => {
                var texture = self.textures.find(x => x.filepath.toLowerCase() === filePath.toLowerCase());
                if (!texture) {
                    let blobUrl = window.URL.createObjectURL(blob);
                    texture = {
                        blobUrl: blobUrl,
                        filepath: filePath
                    } as Texture
                    self.textures.push(texture);                    
                }
                callback(texture.blobUrl);
            });
        }
    }
}


/*
 * MeshLibrary
 * */
export class MeshLibrary {

    loadedMeshStates: Array<any>;
    loadedMeshesNode: BABYLON.TransformNode;
    loadedMeshEntities: Array<any>;
    rootNode: BABYLON.TransformNode;
    instanceNode: BABYLON.TransformNode;

    constructor() {
        this.loadedMeshStates = [] as Array<any>
        this.loadedMeshEntities = [] as Array<any>
    }

    handleLoadedMesh(id: string, loadedMesh: BABYLON.AbstractMesh, shadowGenerator: BABYLON.ShadowGenerator, shadowGenerator2: BABYLON.ShadowGenerator, instance: boolean, callback?: any) {

        var self = this;

        if (loadedMesh) {

            var entityNodes = self.rootNode.getChildTransformNodes(false, function (childMesh) {
                return (childMesh.metadata) ? childMesh.metadata.productNo === id : false
            });
            entityNodes.forEach(function (entityNode) {

                let entityBounds = entityNode.getChildMeshes(true, function (cm) { return cm.id === "entitybounds" })[0] as BABYLON.AbstractMesh;
                if (entityBounds) {
                    entityBounds.setEnabled(false);
                }
                let clonedEntityMesh = entityNode.getChildMeshes(true, function (childMesh) { return childMesh.id === id + "_clone" || childMesh.id === id + "_instance" })[0];
                if (!clonedEntityMesh) {

                    if (instance) {
                        /*let mergedMesh = self.instanceNode.getChildMeshes(false, function (childMesh) { return childMesh.id === loadedMesh.name + "_merged" })[0] as BABYLON.AbstractMesh
                        if (!mergedMesh) {
                            mergedMesh = BABYLON.Mesh.MergeMeshes(loadedMesh.getChildMeshes(false) as BABYLON.Mesh[], true, false, null, false, true) as BABYLON.AbstractMesh;
                            (mergedMesh as BABYLON.AbstractMesh).id = loadedMesh.name + "_merged";
                            (mergedMesh as BABYLON.AbstractMesh).parent = self.instanceNode;
                            (mergedMesh as BABYLON.AbstractMesh).setEnabled(false);
                        }*/

                        var clonedMesh = entityNode.getChildMeshes(false, function (childMesh) { return childMesh.id === loadedMesh.name + "_clone" })[0] as BABYLON.AbstractMesh
                        if (!clonedMesh) {
                            clonedEntityMesh = loadedMesh.instantiateHierarchy(self.instanceNode, null, (src, clone) => {
                            //   clonedEntityMesh = clone as BABYLON.Mesh;
                            //  clonedEntityMesh.id = loadedMesh.name + "_merged";
                            }) as BABYLON.AbstractMesh;
                        
                            clonedEntityMesh.id = loadedMesh.name + "_clone";
                           // clonedEntityMesh.receiveShadows = true;
                        }
                       // clonedEntityMesh = MeshHelper.instanceMesh(id, mergedMesh as BABYLON.Mesh, self.rootNode);

                    } else {
                        clonedEntityMesh = MeshHelper.cloneMesh(id, loadedMesh);
                        clonedEntityMesh.receiveShadows = true;
                    }

                    //  clonedEntityMesh = (!instance) ? MeshHelper.cloneMesh(id, loadedMesh) : MeshHelper.instanceMesh(id, mergedMesh as BABYLON.Mesh, self.rootNode);
                    clonedEntityMesh.parent = entityNode;

                    shadowGenerator.addShadowCaster(clonedEntityMesh, true);
                    // shadowGenerator2.addShadowCaster(clonedEntityMesh, true);

                    clonedEntityMesh.getChildMeshes(false).forEach(function (subMesh) {

                        if (subMesh.material) {
                            subMesh.material.backFaceCulling = false;
                        }

                        if (shadowGenerator) {
                            shadowGenerator.addShadowCaster(subMesh, true);
                        }

                        shadowGenerator.addShadowCaster(subMesh, true);
                        // shadowGenerator2.addShadowCaster(subMesh, true);
                        if (!subMesh.isAnInstance)
                            subMesh.receiveShadows = true;

                        subMesh.reIntegrateRotationIntoRotationQuaternion = true;
                        subMesh.isPickable = true;
                        subMesh.setEnabled(true);

                    });

                    BABYLON.Tags.EnableFor(clonedEntityMesh);
                    BABYLON.Tags.AddTagsTo(clonedEntityMesh, "entitymesh");

                    clonedEntityMesh.setEnabled(true);
                }

                if (callback) {
                    callback(entityNode.id, clonedEntityMesh);
                }

            });
        }
    }

    async loadMeshAsync(id: string, entity: Entity, category: string, rootUrl: string, filename: string, flipped: boolean = false, scene: BABYLON.Scene, instance: boolean, shadowGenerator: BABYLON.ShadowGenerator, shadowGenerator2: BABYLON.ShadowGenerator, progress?: any, callback?: any) {

        var self = this;

        let loadedMesh;

        let loadMeshState = this.loadedMeshStates.find(function (lms) { return lms.id === id });
        if (!loadMeshState) {

            self.loadedMeshEntities.push({ id: id, entityId: entity.id });

            loadMeshState = { id: id, total: 0, received: 0, uniqueId: null, loaded: false };
            self.loadedMeshStates.push(loadMeshState);

            var response = await fetch(AppSettings.Paths.BaseModelsPath + rootUrl + filename);

            if (!response.ok) {
                throw console.log(response.statusText);
            }

            const reader = response.body.getReader();
            const contentLength = response.headers.get('Content-Length');

            let receivedLength = 0;
            let chunks = [];
            while (true) {
                const { done, value } = await reader.read();

                if (done) {
                    loadMeshState.loaded = true;
                    break;
                }

                chunks.push(value);
                receivedLength += value.length;

                loadMeshState.received = receivedLength;
                loadMeshState.total = parseInt(contentLength);

                if (progress) {
                    self.loadedMeshEntities.filter(x => x.id === id).forEach(loadedMeshEntity => {
                        progress({ id: loadedMeshEntity.entityId, received: loadMeshState.received, total: loadMeshState.total });
                    });
                }
            }

            var modelBlob = new Blob(chunks);
            var objectUrl = URL.createObjectURL(modelBlob);

            let result = await BABYLON.SceneLoader.ImportMeshAsync("", "", objectUrl, scene, null, ".glb");
            if (result) {

                loadedMesh = result.meshes[0];
                loadedMesh.id = loadedMesh.name = id;
                loadedMesh.parent = self.loadedMeshesNode;
                loadedMesh.setEnabled(false);

                loadedMesh.getChildMeshes(false).forEach(function (subMesh) {
                    if (category) {
                        BABYLON.Tags.EnableFor(subMesh);
                        BABYLON.Tags.AddTagsTo(subMesh, category);
                    }
                });

                loadMeshState = self.loadedMeshStates.find(function (lms) { return lms.id === id });
                loadMeshState.uniqueId = loadedMesh.uniqueId;

                self.handleLoadedMesh(id, loadedMesh, shadowGenerator, shadowGenerator2, instance, callback);
            }

        } else {

            if (loadMeshState.loaded) {
                let loadedMesh = scene.getMeshByUniqueId(loadMeshState.uniqueId);
                self.handleLoadedMesh(id, loadedMesh, shadowGenerator, shadowGenerator2, instance, callback);
            } else {
                self.loadedMeshEntities.push({ id: id, entityId: entity.id });
            }
        }

    }
}

//Libraries
export const projectLibrary = new ProjectLibrary();
export const configurationLibrary = new ConfigurationLibrary();
export const seriesLibrary = new SeriesLibrary();
export const productLibrary = new ProductLibrary();
export const meshLibrary = new MeshLibrary();
export const materialLibrary = new MaterialLibrary();